<template>
	<div tabindex="0" class="hide-outline">
		<main class="main-view no-top-panel">
			<div>
				<div class="title">
					{{ building.name }}
				</div>
				<div class="top-box">
					<div class="row" :style="mobile">
						<div class="field">
							<label class="label">Namn</label>
							<div class="control">
								<input
									type="text"
									v-model="building.name"
									class="input"
									placeholder="Byggnadsnamn"
									:disabled="!$isAllowedTo('buildings_edit')"
								/>
							</div>
						</div>
						<div class="field description">
							<label class="label">Beskrivning</label>
							<div class="control">
								<input
									type="text"
									v-model="building.description"
									class="input"
									placeholder="Byggnadsbeskrivning"
									:disabled="!$isAllowedTo('buildings_edit')"
								/>
							</div>
						</div>
						<div class="field link-buttons">
							<div class="buttons links" :class="{ 'is-mobile': isMobile }">
								<div class="end-buttons" v-if="!isMobile">
									<button
										class="button is-info"
										@click="saveBuilding"
										v-if="$isAllowedTo('buildings_edit')"
									>
										<span class="icon is-white"><i class="fas fa-save"/></span
										><span>Spara</span></button
									><button class="button is-dark" @click="cancel">
										<span class="icon"><i class="fas fa-backward"/></span>
										<span>Tillbaka</span>
									</button>
								</div>
								<div
									class="buttons"
									:class="{ 'is-mobile': isMobile }"
									v-if="isMobile"
								>
									<button class="button is-info" @click="saveBuilding">
										<span class="icon is-white"><i class="fas fa-save"/></span
										><span>Spara</span>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<TabComponent :tabs="tabs" v-model="selectedTab" />
			<FloorList
				v-if="selectedTab === 0"
				:floors="floors"
				@add="toggleAddFloor"
				@edit="editFloor"
				@delete="askToDelete"
				@navigate="navigate"
				@search="getFloors"
			/>
			<UserList
				v-if="selectedTab === 1"
				:building="building"
				:buildingUsers="building.users"
				@add="addUser"
				@remove="removeUser"
			/>
			<AddFloor
				v-if="showAddFloor"
				:editFloor="selectedFloor"
				@save="saveFloor"
				@cancel="toggleAddFloor"
			/>
		</main>
		<DeleteModal
			v-if="showDelete"
			@confirm="deleteSelected"
			@cancel="cancelDelete"
			:message="deleteMessage"
		/>
	</div>
</template>

<script>
import TabComponent from '@/components/TabComponent';
import FloorList from './components/FloorList';
import AddFloor from './modals/AddFloor';
import UserList from './components/UserList';
import DeleteModal from '@/components/DeleteModal';

import { mapGetters, mapActions, mapState } from 'vuex';

export default {
	components: {
		TabComponent,
		FloorList,
		AddFloor,
		UserList,
		DeleteModal
	},

	data: () => ({
		showAddFloor: false,
		showDelete: false,
		selectedFloor: {},
		selectedTab: 0,
		tabs: [
			{
				name: 'Våningar',
				icon: 'fa-building',
				tag: ''
			},
			{
				name: 'Användare',
				icon: 'fa-users',
				tag: ''
			}
		]
	}),

	watch: {
		'getActiveCustomer.id'() {
			if (this.getActiveCustomer.id !== this.building.customerId) 
				this.$router.push('/buildings');
		}
	},

	computed: {
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('buildingStore', ['building']),
		...mapGetters('floorStore', ['floors']),
		...mapGetters('user', ['getActiveCustomer']),

		...mapState('buildingStore', {
			buildingParams: 'searchParams'
		}),
		...mapState('floorStore', {
			floorParams: 'searchParams'
		}),
		mobile() {
			return this.isMobile ? { display: 'block' } : '';
		},
		deleteMessage() {
			return this.selectedFloor.name
				? `Är du säker på att du vill ta bort ${this.selectedFloor.name}?`
				: '';
		}
	},

	created() {
		const buildingId = this.$route.params.buildingId;
		this.getBuilding(buildingId).then(() => {
			this.floorParams.buildingId = buildingId;
			this.getFloors();
		});
	},

	methods: {
		...mapActions('buildingStore', [
			'getBuilding',
			'updateBuilding',
			'setSelectedBuilding'
		]),
		...mapActions('floorStore', [
			'getFloors',
			'updateFloor',
			'createFloor',
			'deleteFloorWithMap'
		]),
		...mapActions('mapStore', ['createMap', 'setMaps']),
		...mapActions('mapItemStore', ['setMapItems']),
		saveBuilding() {
			this.building.userIds = [];
			this.building.users.forEach(user => {
				this.building.userIds.push(user.id);
			});
			this.updateBuilding(this.building);
		},

		cancel() {
			this.$router.push('/buildings');
		},

		askToDelete(floor) {
			this.showAdd = false;
			Object.assign(this.selectedFloor, floor);
			this.showDelete = true;
		},

		cancelDelete() {
			this.showDelete = false;
			this.selectedFloor = {};
		},

		deleteSelected() {
			this.deleteFloorWithMap(this.selectedFloor).then(() => {
				this.showDelete = false;
				this.selectedFloor = {};
				this.getFloors();
			});
		},

		toggleAddFloor() {
			this.showAddFloor = !this.showAddFloor;
			this.selectedFloor = {};
		},

		saveFloor(floor) {
			if (this.selectedFloor.id) {
				this.updateFloor(floor).then(() => {
					this.showAddFloor = false;
					this.selectedFloor = {};
					this.getFloors();
				});
			} else {
				floor.buildingId = this.building.id;
				this.createFloor(floor).then(newFloor => {
					this.showAddFloor = false;
					this.getFloors().then(() => {
						this.createMap({
							floorId: newFloor.id,
							name: 'Ritning 1'
						});
					});
				});
			}
		},

		editFloor(floor) {
			if (this.selectedFloor == floor) {
				this.selectedFloor = {};
				this.showAddFloor = false;
			} else {
				this.selectedFloor = floor;
				this.showAddFloor = true;
			}
		},

		navigate(id) {
			this.setMaps([]);
			this.setMapItems([]);
			this.$router.push({
				name: 'FloorDetailView',
				params: {
					floorId: id
				}
			});
		},

		addUser(user) {
			this.building.users.push(user);
		},

		removeUser(index) {
			this.building.users.splice(index, 1);
		}
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/colors.scss";

.column .title {
  margin-bottom: 16px;
}
.top-box {
  background: $lightest-grey;
  padding: 10px 5px 10px 5px;
  display: block;
  border-radius: 2px;
  margin-bottom: 10px;
  .row {
    display: flex;
  }
  .field {
    flex: 1;
    &.link-buttons {
      margin-top: 0.8rem;
      flex: 1;
    }
    &.description {
      flex: 2;
    }
    margin-left: 10px;
    margin-right: 10px;
    .label {
      font-size: 12px;
    }
  }
  .input {
    background: white;
    border-radius: 2px;
  }
  .buttons {
    &.links {
      max-width: 100%;
      display: flex;
      justify-content: space-between;
    }
    margin: 0;
    margin-top: 10px;
    margin-left: auto;
    max-width: 118px;
    &.is-mobile {
      margin-top: 0px;
      .button {
        margin-right: 0;
      }
    }
    .button {
      border-radius: 2px;
    }
  }
}
.end-buttons {
  margin-left: auto;
}
</style>
